// @flow
import React, {type Node, useCallback, useState, useEffect} from 'react'
import {type RelayFragmentContainer, createFragmentContainer, graphql} from 'react-relay'

import Heading from 'components/ui/Heading'
import Box from 'components/OrgNav/OrgNavApp/RoleContainer/Sections/ui/Box'
import liftNodes from 'utils/GraphQL/typedLiftNodes'
import CircleSelector, {type Circle} from 'components/circles/CircleSelector'

import type {
  CirclePicker_viewer as Viewer,
} from './__generated__/CirclePicker_viewer.graphql'

type Props = $ReadOnly<{
  viewer: Viewer,
  circleDatabaseId: string | null,
  createProposalForCircleId: (string) => void,
}>

const showCircleInOptions = (circle) => circle.viewerCanCreateProposal || !circle.isGovernanceEnabled

function CirclePicker({circleDatabaseId, createProposalForCircleId, viewer}: Props): Node {
  const [circleIdState, setCircleIdState] = useState(circleDatabaseId)

  useEffect(() => {
    if (circleIdState)
      createProposalForCircleId(circleIdState)
  })

  const circles = liftNodes(viewer.circles)
    .filter(showCircleInOptions)

  const setCircle = useCallback((event) => {
    setCircleIdState(event.target.value)
  }, [setCircleIdState])

  const circleLabel = (circle: Circle) => (
    !circle.isGovernanceEnabled
      ? `${circle.localizedName} (${I18n.t('shared.governance_off')})`
      : circle.localizedName
  )

  const optionDisabled = (circle: Circle) => !circle.isGovernanceEnabled

  return (
    <Box>
      <Heading
        headingLevel={2}
        spacing="xSmall"
        underlined
      >
        {I18n.t('proposals.rewrite.select_input_choose_circle')}
      </Heading>
      <Box.Body
        topPadding
        bottomPadding
      >
        <div className="col-xs-6">
          <CircleSelector
            test-id="field-circle"
            name="circleId"
            placeholder={I18n.t('projects.my_projects.select_circle')}
            circles={circles}
            useAsValue="databaseId"
            label={I18n.t('projects.my_projects.circle')}
            onChange={setCircle}
            value={circleIdState}
            circleLabel={circleLabel}
            optionDisabled={optionDisabled}
          />
        </div>
      </Box.Body>
    </Box>
  )
}

export default (createFragmentContainer(CirclePicker, {
  viewer: graphql`
    fragment CirclePicker_viewer on Person {
      circles(first: 100) {
        edges {
          node {
            viewerCanCreateProposal
            isGovernanceEnabled

            ...CircleSelector_circles
          }
        }
      }
    }
  `,
}): RelayFragmentContainer<typeof(CirclePicker)>)
